/**
 * <dynamic-dates> — fechas estimadas de entrega.
 *
 * El tema trae el marcado de este componente pero no su registro: main.js está
 * ofuscado y la clase nunca llega a definirse, así que la ficha mostraba
 * "Recíbelo entre el - y el -". Esto lo reimplementa por completo.
 *
 * Lee del marcado existente, sin tocar el Liquid del bloque:
 *   data-date-format    day_mm_dd | day_dd_mm | mm_dd | dd_mm
 *   data-day-labels     7 nombres de día separados por coma, empezando en lunes
 *   data-month-labels   12 nombres de mes separados por coma
 *   data-days-to-skip   días de la semana en los que no se envía
 *   data-dates-to-skip  fechas concretas dd/mm a saltar (festivos)
 * y dentro, en [data-dynamic-date]: data-text, data-min-days, data-max-days.
 */
(() => {
  if (customElements.get('dynamic-dates')) return;

  const lista = (valor) =>
    (valor || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      // Los nombres vienen empezando en lunes; getDay() devuelve 0 = domingo.
      this.dias = lista(this.dataset.dayLabels);
      this.meses = lista(this.dataset.monthLabels);
      this.formato = this.dataset.dateFormat || 'dd_mm';
      this.saltarDias = lista(this.dataset.daysToSkip).map((d) => d.toLowerCase());
      this.saltarFechas = lista(this.dataset.datesToSkip).map((d) => d.toLowerCase());

      this.querySelectorAll('[data-dynamic-date]').forEach((nodo) => this.pintar(nodo));
    }

    /** Nombre del día tal y como lo configuró la tienda. */
    nombreDia(fecha) {
      const indice = (fecha.getDay() + 6) % 7; // lunes = 0
      return this.dias[indice] || '';
    }

    /** ¿Se entrega este día? */
    esHabil(fecha) {
      if (this.saltarDias.includes(this.nombreDia(fecha).toLowerCase())) return false;
      const dd = String(fecha.getDate()).padStart(2, '0');
      const mm = String(fecha.getMonth() + 1).padStart(2, '0');
      return !this.saltarFechas.includes(`${dd}/${mm}`);
    }

    /** Suma días naturales y luego empuja hasta el primer día hábil. */
    sumar(dias) {
      const f = new Date();
      f.setDate(f.getDate() + dias);
      let guarda = 0;
      while (!this.esHabil(f) && guarda < 14) {
        f.setDate(f.getDate() + 1);
        guarda++;
      }
      return f;
    }

    formatear(fecha) {
      const dia = fecha.getDate();
      const mes = this.meses[fecha.getMonth()] || '';
      const semana = this.nombreDia(fecha);

      switch (this.formato) {
        case 'day_mm_dd':
          return `${semana}, ${mes} ${dia}`;
        case 'day_dd_mm':
          return `${semana}, ${dia} de ${mes}`;
        case 'mm_dd':
          return `${mes} ${dia}`;
        default: // dd_mm
          return `${dia} de ${mes}`;
      }
    }

    pintar(nodo) {
      const plantilla = nodo.dataset.text;
      const min = parseInt(nodo.dataset.minDays, 10);
      const max = parseInt(nodo.dataset.maxDays, 10);
      if (!plantilla || Number.isNaN(min) || Number.isNaN(max)) return;

      const inicio = this.formatear(this.sumar(min));
      const fin = this.formatear(this.sumar(max));

      nodo.innerHTML = plantilla
        .replaceAll('[start_date]', inicio)
        .replaceAll('[end_date]', fin);
    }
  }

  customElements.define('dynamic-dates', DynamicDates);
})();
